import {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { ProblemAndAnswer } from '@gmm/sdk-voodoo/shared';
import { createNamedContext } from '~/lib/createNamedContext';

import { useToggle } from '../hooks';
import { useLocationEffect } from '../hooks/useLocationEffect';
import { LockedExamProblemParams } from '../problemPreview/hooks/useLockedExamTemplateProblem';

import { Sibling } from './utils';

// Skills can have skill id stored in 'type', an 'id', or skillId, depending on context.
// For example, in skill discovery, type refers to the Tag type, not the Skill type.
// If an exam skill has an id, it's an exam RestoreId.
const getSkillId = (skill: Sibling): string =>
  (skill.skillId || skill.id || skill.type)!;

enum SkillPreviewTab {
  Preview = 'preview',
  Answer = 'answer',
  Students = 'students',
}

interface OpenFullscreenWith {
  studentProblemData: ProblemAndAnswer | null;
  description?: string;
  siblingSkills: Sibling[];
  title?: ReactNode;
  type: string;
  // If this is present, we're previewing a student's exam problem
  studentInTestId?: string;
  examRestoreId?: string;
  // If this is present, we're previewing Current Status
  studentInClassId?: string;
  helpRequestedProblemData?: ProblemAndAnswer;
  // If this is present, we're previewing a locked exam
  lockedExamProblemParams?: LockedExamProblemParams;
  // If this is present, we're viewing a locked exam problem from Skill Discovery
  isSkillDiscoveryLockedExamProblem?: boolean;
}

interface Context {
  currentIndex: number;
  studentProblemData: ProblemAndAnswer | null;
  currentType: string;
  description?: string;
  isModalOpen: boolean;
  onClose: () => void;
  openFullscreenWith: (args: OpenFullscreenWith) => void;
  setOnFullscreenClose: (
    onFullscreenClose: (() => OnFullscreenClose) | undefined,
  ) => void;
  setTitle: Dispatch<SetStateAction<ReactNode | undefined>>;
  showNextSibling: () => void;
  showPreviousSibling: () => void;
  siblingSkills: Sibling[];
  tab: string;
  title: ReactNode | undefined;
  studentInTestId?: string;
  examRestoreId?: string;
  studentInClassId?: string;
  // supports 'Problem #32' for arrowing through exam problems
  problemTabText?: string;
  helpRequestedProblemData?: ProblemAndAnswer;
  lockedExamProblemParams?: LockedExamProblemParams;
  hasSiblings: boolean;
  isFirstSibling: boolean;
  isLastSibling: boolean;
  isSkillDiscoveryLockedExamProblem?: boolean;
}

type OnFullscreenClose = (skillType: string) => void;

const FullscreenContext = createNamedContext<Context>(
  'FullscreenContext',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  null as any,
);

export const FullscreenProvider: FC = ({ children }) => {
  const [siblingSkills, setSiblingSkills] = useState<Sibling[]>([]);
  const [currentType, setCurrentType] = useState('');
  const [description, setDescription] = useState<string | undefined>();
  const [title, setTitle] = useState<ReactNode | undefined>();
  const [studentInTestId, setStudentInTestId] = useState<string | undefined>();
  const [examRestoreId, setExamRestoreId] = useState<string | undefined>();
  const [helpRequestedProblemData, sethelpRequestedProblemData] = useState<
    ProblemAndAnswer | undefined
  >(undefined);
  const [studentInClassId, setStudentInClassId] = useState<
    string | undefined
  >();
  const [lockedExamProblemParams, setLockedExamProblemParams] = useState<
    LockedExamProblemParams | undefined
  >();
  const [
    isSkillDiscoveryLockedExamProblem,
    setIsSkillDiscoveryLockedExamProblem,
  ] = useState<boolean>(false);
  const isLockedExam = !!lockedExamProblemParams;
  const hasSiblings =
    siblingSkills.length > 1 ||
    (isLockedExam &&
      siblingSkills.length === 1 &&
      Number(siblingSkills[0].instances!) > 1);
  const [isFirstSibling, setIsFirstSibling] = useState(false);
  const [isLastSibling, setIsLastSibling] = useState(false);

  const [problemTabText, setProblemTabText] = useState<string | undefined>();
  const [isModalOpen, showModal, closeModal] = useToggle();
  const [tab, setTab] = useState(SkillPreviewTab.Preview);
  const onFullscreenCloseRef = useRef<
    ((skillType: string) => void) | undefined
  >();
  const setOnFullscreenClose = (
    onFullscreenClose: (() => OnFullscreenClose) | undefined,
  ): void => {
    onFullscreenCloseRef.current =
      typeof onFullscreenClose === 'function'
        ? onFullscreenClose()
        : onFullscreenClose;
  };

  const [studentProblemData, setCurrentProblemData] =
    useState<ProblemAndAnswer | null>(null);

  const openFullscreenWith = ({
    studentProblemData,
    description,
    siblingSkills,
    title,
    type,
    studentInTestId,
    examRestoreId,
    studentInClassId,
    helpRequestedProblemData,
    lockedExamProblemParams,
    isSkillDiscoveryLockedExamProblem,
  }: OpenFullscreenWith): void => {
    if (tab === SkillPreviewTab.Students) {
      setTab(SkillPreviewTab.Preview);
    }

    showModal();
    setCurrentProblemData(studentProblemData);
    setCurrentType(type);
    setDescription(description);
    setSiblingSkills(siblingSkills);
    setTitle(title);
    setStudentInTestId(studentInTestId);
    setExamRestoreId(examRestoreId);
    setStudentInClassId(studentInClassId);
    sethelpRequestedProblemData(helpRequestedProblemData);
    setLockedExamProblemParams(lockedExamProblemParams);
    setIsSkillDiscoveryLockedExamProblem(!!isSkillDiscoveryLockedExamProblem);
  };

  // teacher viewing a student's exam problem
  const isStudentExam = !!examRestoreId;

  let currentIndex = -1;
  const exam = !!examRestoreId;

  if (exam) {
    currentIndex = siblingSkills.findIndex(skill => skill.id === examRestoreId);
  } else {
    currentIndex = siblingSkills.findIndex(
      skill => getSkillId(skill) === currentType,
    );
  }

  useEffect(() => {
    if (isStudentExam) {
      const index = siblingSkills.findIndex(
        skill => skill.id === examRestoreId,
      );

      setIsFirstSibling(index === 0);
      setIsLastSibling(index === siblingSkills.length - 1);
      setProblemTabText(`Problem #${index + 1}`);
    } else if (isLockedExam) {
      const current = siblingSkills.find(
        skill => getSkillId(skill) === getSkillId(lockedExamProblemParams),
      );
      let priorInstances = 0;
      let totalInstances = 0;
      let found = false;

      for (let i = 0; i < siblingSkills.length; i++) {
        if (current && getSkillId(siblingSkills[i]) === getSkillId(current)) {
          found = true;
        }

        if (!found) priorInstances += Number(siblingSkills[i].instances!);
        totalInstances += Number(siblingSkills[i].instances!);
      }

      const number =
        priorInstances + lockedExamProblemParams.positionWithinSkill + 1;

      setIsFirstSibling(number === 1);
      setIsLastSibling(number === totalInstances);
      setProblemTabText(`Problem #${number}`);
    } else {
      setProblemTabText(undefined);
      setIsFirstSibling(currentIndex === 0);
      setIsLastSibling(currentIndex === siblingSkills.length - 1);
    }
  }, [isStudentExam, siblingSkills, examRestoreId, lockedExamProblemParams]);

  const showNextSibling = (): void => {
    if (currentIndex === -1) return;

    if (lockedExamProblemParams) {
      showNextLockedExamSibling();

      return;
    }

    if (currentIndex === siblingSkills.length - 1) return;

    const next = siblingSkills[currentIndex + 1];

    updateSkillContext(next);
  };

  const showNextLockedExamSibling = (): void => {
    if (!lockedExamProblemParams || siblingSkills.length === 0) return;

    const current = siblingSkills.find(
      skill => getSkillId(skill) === getSkillId(lockedExamProblemParams),
    );

    if (!current) return;

    const { positionWithinSkill } = lockedExamProblemParams;

    if (positionWithinSkill + 1 < (current.instances ?? 0)) {
      setLockedExamProblemParams({
        ...lockedExamProblemParams,
        positionWithinSkill: positionWithinSkill + 1,
      });
    } else {
      if (currentIndex === siblingSkills.length - 1) return;

      const next = siblingSkills[currentIndex + 1];

      setLockedExamProblemParams({
        ...lockedExamProblemParams,
        skillId: getSkillId(next),
        positionWithinSkill: 0,
      });

      updateSkillContext(next);
    }
  };

  const updateSkillContext = (next: Sibling): void => {
    setDescription(next.description || next.name);
    const nextType = getSkillId(next);

    setCurrentType(nextType);
    if (isStudentExam) setExamRestoreId(next.id);
    setTab(SkillPreviewTab.Preview);

    if (!isLockedExam) {
      const index = siblingSkills.findIndex(
        skill => getSkillId(skill) === nextType,
      );

      setIsFirstSibling(index === 0);
      setIsLastSibling(index === siblingSkills.length - 1);
    }
  };

  const showPreviousSibling = (): void => {
    if (currentIndex < 0) return;
    if (currentIndex === 0 && !lockedExamProblemParams) return;
    if (
      currentIndex === 0 &&
      lockedExamProblemParams?.positionWithinSkill === 0
    )
      return;

    if (lockedExamProblemParams) {
      showPreviousLockedExamSibling();

      return;
    }

    const prev = siblingSkills[currentIndex - 1];

    updateSkillContext(prev);
  };

  const showPreviousLockedExamSibling = (): void => {
    if (!lockedExamProblemParams || siblingSkills.length === 0) return;

    const current = siblingSkills.find(
      skill => getSkillId(skill) === getSkillId(lockedExamProblemParams),
    );

    if (!current) return;

    const { positionWithinSkill } = lockedExamProblemParams;

    if (positionWithinSkill > 0) {
      setLockedExamProblemParams({
        ...lockedExamProblemParams,
        positionWithinSkill: positionWithinSkill - 1,
      });
    } else {
      if (currentIndex <= 0) return;

      const prev = siblingSkills[currentIndex - 1];

      setLockedExamProblemParams({
        ...lockedExamProblemParams,
        skillId: getSkillId(prev),
        positionWithinSkill: (prev.instances ?? 0) - 1,
      });

      updateSkillContext(prev);
    }
  };

  const onClose = useCallback((): void => {
    closeModal();
    onFullscreenCloseRef.current?.(currentType);
    setOnFullscreenClose(undefined);
  }, [closeModal, currentType]);

  useLocationEffect(() => {
    setTab(SkillPreviewTab.Preview);
    setOnFullscreenClose(undefined);
  });

  useLocationEffect((_, action) => {
    if (action === 'POP') {
      onClose();
    }
  });

  const value = {
    currentIndex,
    currentType,
    description,
    studentProblemData,
    isModalOpen,
    openFullscreenWith,
    onClose,
    setOnFullscreenClose,
    setTitle,
    showNextSibling,
    showPreviousSibling,
    siblingSkills,
    tab,
    title,
    studentInTestId,
    examRestoreId,
    studentInClassId,
    problemTabText,
    helpRequestedProblemData,
    lockedExamProblemParams,
    hasSiblings,
    isFirstSibling,
    isLastSibling,
    isSkillDiscoveryLockedExamProblem,
  };

  return (
    <FullscreenContext.Provider value={value}>
      {children}
    </FullscreenContext.Provider>
  );
};

export const useFullscreenContext = (): Context =>
  useContext(FullscreenContext);
